import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Chip, Button, Card, CardContent,
  CardActions, Divider, TextField, InputAdornment
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SearchIcon from "@mui/icons-material/Search";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import { exportToCSV } from "../../utils/tableUtils";

const tagColors = {
  Planning: { bg: "#e3f2fd", color: "#2196f3" },
  Published: { bg: "#e8f5e9", color: "#4caf50" },
  Draft: { bg: "#fff3e0", color: "#ff9800" },
};

const fallback = [
  { id: 1, title: "Tamil Poetry Project", tags: ["Planning", "Nilai 5"], status: "Published", description: "Students are required to compose original Tamil poems on cultural themes. Each poem should have a minimum of 12 lines with proper meter and rhythm.", createdAt: "2025-03-01" },
  { id: 2, title: "Literature Analysis Assignment", tags: ["Planning", "Nilai 3"], status: "Published", description: "Analyze a selected Tamil literary work. The analysis should cover themes, characters, and language usage. Submit a 5-page report with citations.", createdAt: "2025-03-10" },
  { id: 3, title: "Grammar Workbook Project", tags: ["Published", "Nilai 2"], status: "Published", description: "Create a comprehensive grammar workbook covering verb conjugations, noun forms, and sentence structures. Include examples and exercises.", createdAt: "2025-03-15" },
  { id: 4, title: "Tamil Cultural Festival Presentation", tags: ["Planning", "All Grades"], status: "Draft", description: "Prepare a presentation on a Tamil cultural festival. Include history, traditions, significance, and modern celebrations. Must include visual aids.", createdAt: "2025-03-20" },
];

const ProjectGuidelines = () => {
  const [guidelines, setGuidelines] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadGuidelines = useCallback(async () => {
    try {
      const response = await fetchData("student/project-guidelines?status=published&limit=50");
      if (response?.data) {
        const data = Array.isArray(response.data) ? response.data : response.data.data || [];
        setGuidelines(data.length > 0 ? data : fallback);
      } else {
        setGuidelines(fallback);
      }
    } catch (_) {
      setGuidelines(fallback);
    }
  }, []);

  useEffect(() => { loadGuidelines(); }, [loadGuidelines]);

  const handleToastClose = () => setToast({ ...toast, open: false });

  // Filter guidelines across title, description, status, tags
  const filteredGuidelines = filterText.trim()
    ? guidelines.filter((g) => {
        const lower = filterText.toLowerCase();
        const tags = Array.isArray(g.tags) ? g.tags.join(" ") : (g.tags || "");
        return (
          (g.title || "").toLowerCase().includes(lower) ||
          (g.description || "").toLowerCase().includes(lower) ||
          (g.status || "").toLowerCase().includes(lower) ||
          tags.toLowerCase().includes(lower)
        );
      })
    : guidelines;

  const handleExport = () => {
    const exportCols = [
      { label: "Title", field: "title" },
      { label: "Status", field: "status" },
      { label: "Tags", field: "tagsStr" },
      { label: "Date", field: "dateStr" },
      { label: "Description", field: "description" },
    ];
    const exportData = filteredGuidelines.map((g) => ({
      title: g.title || "—",
      status: g.status || "—",
      tagsStr: Array.isArray(g.tags) ? g.tags.join(", ") : (g.tags || "—"),
      dateStr: g.createdAt ? new Date(g.createdAt).toLocaleDateString("en-GB") : "—",
      description: g.description || "—",
    }));
    exportToCSV(exportCols, exportData, "project_guidelines.csv");
  };

  const styles = {
    container: { textAlign: "left", padding: "1rem" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 },
    badge: { backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" },
    actionBtn: { border: "1px solid #D5D7DA", borderRadius: "8px", color: "#8F95B2", textTransform: "none", fontSize: "13px", padding: "6px 14px" },
    guidelineCard: { borderRadius: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%", display: "flex", flexDirection: "column", transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(80,56,237,0.12)" } },
    cardHeader: { display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5 },
    guidelineIcon: { width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "#EAF1F8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    viewBtn: { background: "linear-gradient(90deg, #4A7BA8, #244C77)", color: "#fff", fontSize: "13px", textTransform: "none", padding: "6px 16px", borderRadius: "8px" },
  };

  return (
    <div style={styles.container}>
      <Toast open={toast.open} handleClose={handleToastClose} severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0, 0, 0, 0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Project Guidelines
      </p>

      <Box sx={styles.headerRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <h2 style={{ margin: 0 }}>Project Guidelines</h2>
          <Chip label={`${filteredGuidelines.length} Guidelines`} sx={styles.badge} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<FilterListIcon />} sx={{ ...styles.actionBtn, ...(showFilter ? { backgroundColor: "#f0edff", color: "#244C77", borderColor: "#244C77" } : {}) }} onClick={() => setShowFilter((v) => !v)}>Filters</Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} sx={styles.actionBtn} onClick={handleExport}>Export</Button>
        </Box>
      </Box>

      {showFilter && (
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small" fullWidth placeholder="Search by title, status, tags..."
            value={filterText} onChange={(e) => setFilterText(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#8F95B2", fontSize: "18px" }} /></InputAdornment> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px", "& fieldset": { borderColor: "#D5D7DA" } } }}
          />
        </Box>
      )}

      <Grid container spacing={3}>
        {filteredGuidelines.map((guideline, index) => {
          const tags = Array.isArray(guideline.tags) ? guideline.tags : (guideline.tags || "").split(",").filter(Boolean);
          const statusColor = tagColors[guideline.status] || { bg: "#f5f5f5", color: "#8F95B2" };

          return (
            <Grid item xs={12} sm={6} md={4} key={guideline.id || index}>
              <Card elevation={0} sx={styles.guidelineCard}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={styles.cardHeader}>
                    <Box sx={styles.guidelineIcon}>
                      <BookmarkIcon sx={{ color: "#244C77", fontSize: "22px" }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: "bold", fontSize: "15px", lineHeight: 1.3, mb: 0.5 }}>
                        {guideline.title}
                      </Typography>
                      <Chip
                        label={guideline.status || "Published"}
                        size="small"
                        sx={{ backgroundColor: statusColor.bg, color: statusColor.color, fontWeight: "bold", fontSize: "11px", height: "20px" }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                    {tags.map((tag, i) => {
                      const tagColor = tagColors[tag.trim()] || { bg: "#f5f5f5", color: "#6B7280" };
                      return (
                        <Chip key={i} label={tag.trim()} size="small" sx={{ backgroundColor: tagColor.bg, color: tagColor.color, fontSize: "11px", height: "20px" }} />
                      );
                    })}
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Typography sx={{ fontSize: "13px", color: "#6B7280", lineHeight: 1.6 }}>
                    {guideline.description
                      ? guideline.description.length > 150
                        ? guideline.description.substring(0, 150) + "..."
                        : guideline.description
                      : "No description available."}
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2 }}>
                    <CalendarTodayIcon sx={{ fontSize: "14px", color: "#8F95B2" }} />
                    <Typography sx={{ fontSize: "12px", color: "#8F95B2" }}>
                      {guideline.createdAt
                        ? new Date(guideline.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                        : "—"}
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button size="small" sx={styles.viewBtn} onClick={() => { if (guideline.url) window.open(guideline.url, "_blank"); }} fullWidth>
                    View Guideline
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredGuidelines.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8, color: "#8F95B2" }}>
          <BookmarkIcon sx={{ fontSize: "48px", opacity: 0.3, mb: 1 }} />
          <Typography>{filterText ? "No guidelines match your search" : "No project guidelines available"}</Typography>
        </Box>
      )}
    </div>
  );
};

export default ProjectGuidelines;
